/**
 * FikoAi3DKasa — uretilen 3D modeli tarayicida gecici olarak saklar (IndexedDB).
 *
 * NEDEN: Uretilen GLB/STL yalnizca bellekte bir Blob. Kullanici sonucu
 * "/🧩/🌌" 3D goruntuleyicide YENI SEKMEDE acmak isterse blob URL'i o sekmede
 * gecersizdir. Bu yuzden model kasaya yazilir, yeni sekmeye sadece anahtar
 * gecirilir (?fikoAi3D=xxx) ve orada kasadan okunur. Sunucuya yukleme yok.
 *
 * Kayitlar 1 gun sonra otomatik temizlenir.
 */

const VT_ADI = 'fikoyt-ai3d';
const DEPO = 'modeller';
const OMUR_MS = 24 * 60 * 60 * 1000;

export interface FikoAi3DKayit {
  anahtar: string;
  ad: string;
  blob: Blob;
  hedefBoyutMm: number;
  zaman: number;
}

function vtAc(): Promise<IDBDatabase> {
  return new Promise((cozum, hata) => {
    const istek = indexedDB.open(VT_ADI, 1);
    istek.onupgradeneeded = () => {
      const vt = istek.result;
      if (!vt.objectStoreNames.contains(DEPO)) vt.createObjectStore(DEPO, { keyPath: 'anahtar' });
    };
    istek.onsuccess = () => cozum(istek.result);
    istek.onerror = () => hata(istek.error || new Error('IndexedDB acilamadi'));
  });
}

function islem<T>(mod: IDBTransactionMode, is: (depo: IDBObjectStore) => IDBRequest): Promise<T> {
  return vtAc().then(vt => new Promise<T>((cozum, hata) => {
    const tx = vt.transaction(DEPO, mod);
    const istek = is(tx.objectStore(DEPO));
    istek.onsuccess = () => cozum(istek.result as T);
    istek.onerror = () => hata(istek.error || new Error('Kasa islemi basarisiz'));
    tx.oncomplete = () => vt.close();
  }));
}

/** Suresi dolmus kayitlari siler (sessiz). */
async function temizle(): Promise<void> {
  try {
    const hepsi = await islem<FikoAi3DKayit[]>('readonly', d => d.getAll());
    const simdi = Date.now();
    for (const k of hepsi || []) {
      if (simdi - (k.zaman || 0) > OMUR_MS) await fikoAi3DKasadanSil(k.anahtar);
    }
  } catch { /* kasa yoksa/kapaliysa yoksay */ }
}

/** Modeli kasaya koyar, anahtarini dondurur. */
export async function fikoAi3DKasayaKoy(blob: Blob, ad: string, hedefBoyutMm = 100): Promise<string> {
  await temizle();
  const anahtar = 'm' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  const kayit: FikoAi3DKayit = { anahtar, ad, blob, hedefBoyutMm, zaman: Date.now() };
  await islem('readwrite', d => d.put(kayit));
  return anahtar;
}

/** Anahtara gore modeli okur (yoksa null). */
export async function fikoAi3DKasadanAl(anahtar: string): Promise<FikoAi3DKayit | null> {
  try { return (await islem<FikoAi3DKayit>('readonly', d => d.get(anahtar))) || null; }
  catch { return null; }
}

export async function fikoAi3DKasadanSil(anahtar: string): Promise<void> {
  try { await islem('readwrite', d => d.delete(anahtar)); } catch { /* yoksay */ }
}

/** 3D goruntuleyici adresi: /🧩/🌌?fikoAi3D=<anahtar> */
export function fikoAi3DGoruntuleyiciAdres(anahtar: string): string {
  return '/' + encodeURIComponent('🧩') + '/' + encodeURIComponent('🌌')
    + '?fikoAi3D=' + encodeURIComponent(anahtar);
}
